import React, { FC, useRef, useState, useEffect } from 'react';
import { Box, CircularProgress, IconButton, Stack } from '@mui/material';
import { NoteReporterBar } from './NoteReporterBar';
import { User } from '@/_models/User';
import { toast } from 'react-toastify';
import WaveSurferPlayer from '@/components/common/AudioPlayer/WaveSurferPlayer';
import { FormHelperText } from '@mui/material';
import Icon from '@/units/Icon';
import Editor from './Editor';
import { uniqueId } from 'lodash';
import { renderWithLoader } from './helpers/UploadProgress';

const maxDescriptionLength = 300;

declare const MediaRecorder: any;

interface IDescriptionFieldProps {
  collaborators: User[];
  isNewThread: boolean;
  editMode: boolean;
  foundUser: User;
  focused: boolean;
  setFocused: (value: boolean) => void;
  setDescription: (value: any) => void;
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  addButtonDisabled: boolean;
  setAddButtonDisabled: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  handleAdd: ({}) => Promise<string>;
  clearInput: () => void;
  description: string;
  setAudioURLs: (data: any) => void;
  previewSrcs: any[];
  loadingFiles: any[];
  audioURLs: any[];
  mediaUrls?: { src: string; flaggedToDelete: boolean }[];
  setMediaUrls?: (state: any) => void;
  isLoading: boolean;
  removeReview: (data: { name?: string; lastModified?: number }) => void;
  removeAudio: (data: { lastModified: number }) => void;
  handleFileChange: (data: any) => void;
  descriptionLength: number;
  setDescriptionLength?: (length: number) => void;
  descriptionError: string;
}

export const DescriptionField: FC<IDescriptionFieldProps> = ({
  collaborators,
  isNewThread,
  editMode,
  foundUser,
  focused,
  setFocused,
  setFiles,
  addButtonDisabled,
  setAddButtonDisabled,
  setIsLoading,
  handleAdd,
  clearInput,
  description,
  setDescription,
  setAudioURLs,
  previewSrcs,
  loadingFiles,
  audioURLs,
  mediaUrls,
  setMediaUrls,
  isLoading,
  removeReview,
  removeAudio,
  handleFileChange,
  descriptionLength,
  setDescriptionLength,
  descriptionError,
}) => {
  const [recording, setRecording] = useState(false);
  const [audioMedia, setAudioMedia] = useState(mediaUrls?.filter((urlObj) => urlObj.src.includes('.wav')));
  const [galleryMedia, setGalleryMedia] = useState(mediaUrls?.filter((urlObj) => !urlObj.src.includes('.wav')));
  const [anchorEl, setAnchorEl] = useState(null);
  const mediaRecorder = useRef<any | null>(null);
  const fileInputRef = useRef(null);
  const textContainerRef = useRef(null);

  const handleDelete = (urlObj) => {
    setAudioMedia((prevState) => prevState.map((state) => (state.src === urlObj.src ? { ...state, flaggedToDelete: true } : state)));
    setGalleryMedia((prevState) => prevState.map((state) => (state.src === urlObj.src ? { ...state, flaggedToDelete: true } : state)));
    setMediaUrls((prevState) => prevState.map((state) => (state.src === urlObj.src ? { ...state, flaggedToDelete: true } : state)));
  };

  const countSymbolsLeft = () => {
    return maxDescriptionLength - descriptionLength;
  };

  const stopRecording = () => {
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder.current = new MediaRecorder(stream);

    mediaRecorder.current.ondataavailable = (event) => {
      const tempaudioName = uniqueId();
      const audioBlob = new Blob([event.data], { type: 'audio/wav' });
      const audioFile = new File([audioBlob], `${tempaudioName}recording.wav`, {
        type: 'audio/wav',
      });

      setFiles((prevUploads) => [...prevUploads, audioFile]);
      setAudioURLs((prevUrls) => [...prevUrls, { lastModified: audioFile.lastModified, src: URL.createObjectURL(audioBlob) }]);
    };

    mediaRecorder.current.start();

    setRecording(true);
    setFocused(true);
  };

  useEffect(() => {
    if (textContainerRef.current && focused) {
      textContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focused]);

  const handleFileIconClick = () => {
    fileInputRef.current.click();
  };

  const handleAtIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Box
        ref={textContainerRef}
        sx={{
          width: '100%',
          background: 'white',
          outline: !!descriptionError && isNewThread ? '1px solid red' : focused ? '1px solid blue' : '1px solid #D0D5DD',
          borderRadius: '12px',
          padding: '12px',
          '&:hover': {
            outlineColor: 'blue',
          },
        }}
      >
        {!isNewThread && !editMode && focused && (
          <Box
            sx={{
              maxHeight: 'fit-content',
              height: 'fit-content',
              width: '100%',
              justifyContent: 'flex-start',
              gap: 1,
              flexWrap: 'wrap',
              paddingTop: '10px',
              position: 'relative',
            }}
          >
            <NoteReporterBar name={foundUser?.name} editMode={editMode} isNewThread={isNewThread} />
          </Box>
        )}
        <Box sx={{position: 'relative'}}>
          <Editor
            setDescription={setDescription}
            description={description}
            collaborators={collaborators}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setFocused={setFocused}
            focused={focused}
            recording={recording}
            stopRecording={stopRecording}
            setDescriptionLength={setDescriptionLength}
            handleFileChange={handleFileChange}
            isNewThread={isNewThread}
          />
          {!!descriptionError && isNewThread && (
            <Box sx={{position: 'absolute', right: '0', top: '14px'}}>
              <i className="fa-regular fa-circle-exclamation" style={{ color: 'red', fontSize: '16px'}}/>
            </Box>

          )}

          {(focused || isNewThread || editMode) && (
            <>
              {(!!previewSrcs.length || !!audioURLs.length || !!mediaUrls?.length) && (
                <Box
                  sx={{
                    maxHeight: 'fit-content',
                    height: 'fit-content',
                    marginBottom: '14px',
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 1,
                    flexWrap: 'wrap',
                    position: 'relative',
                  }}
                >
                  {!!previewSrcs?.length && (
                    <Stack direction="row" gap={1} mb="14px" width="100%" sx={{ flexWrap: 'wrap' }}>
                      {previewSrcs.map((preview) => {
                        let fileExtension: string;
                        if (preview.name.endsWith('.jpg') || preview.name.endsWith('.png') || preview.name.endsWith('.gif') || preview.name.endsWith('.webp')) {
                          fileExtension = 'image';
                        }
                        if (preview.name.endsWith('.mp4') || preview.name.endsWith('.webm') || preview.name.endsWith('.ogg')) {
                          fileExtension = 'video';
                        }
                        if (preview.name.endsWith('.pdf')) {
                          fileExtension = 'document';
                        }
                        return (
                          <Stack key={`${preview.name}_${preview.lastModified}`} position="relative">
                            {fileExtension === 'video' &&
                              renderWithLoader(
                                () => (
                                  <Box
                                    sx={{
                                      width: '60px',
                                      height: '60px',
                                      background: '#F4F5F2',
                                      border: '1px solid #D1D1D1',
                                      borderRadius: '8px',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Icon.Video stroke="#000000" />
                                  </Box>
                                ),
                                isLoading,
                              )}
                            {fileExtension === 'document' &&
                              renderWithLoader(
                                () => (
                                  <Box
                                    sx={{
                                      width: '60px',
                                      height: '60px',
                                      background: '#F4F5F2',
                                      border: '1px solid #D1D1D1',
                                      borderRadius: '8px',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Icon.Document stroke="#000000" />
                                  </Box>
                                ),
                                isLoading,
                              )}
                            {fileExtension === 'image' &&
                              renderWithLoader(
                                () => (
                                  <img
                                    src={preview.src}
                                    alt={`File Preview ${preview.name}`}
                                    width="60px"
                                    height="60px"
                                    style={{ border: '1px solid #D1D1D1', borderRadius: '8px' }}
                                  />
                                ),
                                isLoading,
                              )}
                            <IconButton
                              onClick={() => removeReview({ name: preview.name, lastModified: preview.lastModified })}
                              style={{
                                position: 'absolute',
                                top: '-15px',
                                right: '-15px',
                                cursor: 'pointer',
                                color: '#2055FF',
                                fontSize: '32px',
                              }}
                              sx={{
                                '&:hover': {
                                  background: 'none',
                                },
                              }}
                            >
                              <i className="fa-light fa-circle-xmark" style={{ fontSize: '22px', background: '#EAECF0', borderRadius: '50%' }} />
                            </IconButton>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}

                  {audioURLs?.map((audioURL) =>
                    audioURL ? (
                      <Stack key={audioURL.lastModified} position="relative" width="100%">
                        <WaveSurferPlayer
                          url={audioURL.src}
                          waveColor="#D0D5DD"
                          progressColor="#2055FF"
                          barWidth={2}
                          barGap={2}
                          barRadius={4}
                          barHeight={10}
                          wavefHeight={33}
                          playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                          containerSx={{
                            padding: '10px',
                            height: '70px',
                            maxHeight: '70px',
                            width: '100%',
                            gap: '6px',
                            background: '#F9FAFB',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            position: 'relative',
                          }}
                        />
                        {isLoading && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              color: '#FFFFFF',
                            }}
                          >
                            <CircularProgress color="inherit" size={24} />
                          </Box>
                        )}
                        <IconButton
                          onClick={() => removeAudio({ lastModified: audioURL.lastModified })}
                          style={{
                            position: 'absolute',
                            top: '-15px',
                            right: '-15px',
                            cursor: 'pointer',
                            color: '#2055FF',
                            fontSize: '32px',
                          }}
                          sx={{
                            '&:hover': {
                              background: 'none',
                            },
                          }}
                        >
                          <i className="fa-light fa-circle-xmark" style={{ fontSize: '22px', background: '#EAECF0', borderRadius: '50%' }} />
                        </IconButton>
                      </Stack>
                    ) : null,
                  )}
                  {!!audioMedia?.length &&
                    audioMedia?.map((urlObj, i) => {
                      if (urlObj.flaggedToDelete) return null;
                      return (
                        <Stack key={i} position="relative" width="100%" mb={1}>
                          <WaveSurferPlayer
                            url={`${process.env.REACT_APP_AWS}/${urlObj.src}`}
                            waveColor="#D0D5DD"
                            progressColor="#2055FF"
                            barWidth={2}
                            barGap={2}
                            barRadius={4}
                            barHeight={12}
                            wavefHeight={37}
                            playPauseButtonSx={{ color: '#2055FF', padding: 0, height: '30px' }}
                            containerSx={{
                              background: '#F9FAFB',
                              border: '1px solid #EAECF0',
                              borderRadius: '8px',
                              padding: '10px',
                              height: 'auto',
                            }}
                          />
                          <IconButton
                            onClick={() => handleDelete(urlObj)}
                            style={{
                              position: 'absolute',
                              top: '-15px',
                              right: '-15px',
                              cursor: 'pointer',
                              color: '#2055FF',
                              fontSize: '32px',
                            }}
                            sx={{
                              '&:hover': {
                                background: 'none',
                              },
                            }}
                          >
                            <i className="fa-light fa-circle-xmark" style={{ fontSize: '22px', background: '#EAECF0', borderRadius: '50%' }} />
                          </IconButton>
                        </Stack>
                      );
                    })}
                  {!!galleryMedia?.length && (
                    <Stack direction="row" gap={1} width="100%" sx={{ flexWrap: 'wrap' }}>
                      {galleryMedia?.map((urlObj, i) => {
                        if (urlObj.flaggedToDelete) return null;
                        let fileExtension: string;
                        if (urlObj?.src.endsWith('.jpg') || urlObj?.src.endsWith('.png') || urlObj?.src.endsWith('.gif') || urlObj?.src.endsWith('.webp')) {
                          fileExtension = 'image';
                        }
                        if (urlObj?.src.endsWith('.mp4') || urlObj?.src.endsWith('.webm') || urlObj?.src.endsWith('.ogg')) {
                          fileExtension = 'video';
                        }
                        if (urlObj?.src.endsWith('.pdf')) {
                          fileExtension = 'document';
                        }
                        return (
                          <Stack key={i} position="relative">
                            {fileExtension === 'video' && (
                              <Box
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  background: '#F4F5F2',
                                  border: '1px solid #D1D1D1',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Icon.Video stroke="#000000" />
                              </Box>
                            )}
                            {fileExtension === 'document' && (
                              <Box
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  background: '#F4F5F2',
                                  border: '1px solid #D1D1D1',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Icon.Document stroke="#000000" />
                              </Box>
                            )}
                            {fileExtension === 'image' && (
                              <img
                                src={`${process.env.REACT_APP_AWS}/${urlObj.src}`}
                                alt={`Preview ${i}`}
                                width="60px"
                                height="60px"
                                style={{ border: '1px solid #D1D1D1', borderRadius: '8px' }}
                              />
                            )}
                            <IconButton
                              onClick={() => handleDelete(urlObj)}
                              style={{
                                position: 'absolute',
                                top: '-15px',
                                right: '-15px',
                                cursor: 'pointer',
                                color: '#2055FF',
                                fontSize: '32px',
                              }}
                              sx={{
                                '&:hover': {
                                  background: 'none',
                                },
                              }}
                            >
                              <i className="fa-light fa-circle-xmark" style={{ fontSize: '22px', background: '#EAECF0', borderRadius: '50%' }} />
                            </IconButton>
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              )}
              {!!loadingFiles?.length && (
                <Stack direction="row" gap={1} mb="14px" width="100%" sx={{ flexWrap: 'wrap' }}>
                  {loadingFiles.map((file) => {
                    let fileExtension = '';
                    if (file.name.endsWith('.jpg') || file.name.endsWith('.png') || file.name.endsWith('.gif') || file.name.endsWith('.webp')) {
                      fileExtension = 'image';
                    }
                    if (file.name.endsWith('.mp4') || file.name.endsWith('.webm') || file.name.endsWith('.ogg')) {
                      fileExtension = 'video';
                    }
                    if (file.name.endsWith('.pdf')) {
                      fileExtension = 'document';
                    }

                    const isLoading = loadingFiles.includes(file);

                    return (
                      <Stack key={`${file.name}_${file.lastModified}`} position="relative">
                        {fileExtension === 'video' &&
                          renderWithLoader(
                            () => (
                              <Box
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  background: '#F4F5F2',
                                  border: '1px solid #D1D1D1',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Icon.Video stroke="#000000" />
                              </Box>
                            ),
                            isLoading,
                          )}
                        {fileExtension === 'document' &&
                          renderWithLoader(
                            () => (
                              <Box
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  background: '#F4F5F2',
                                  border: '1px solid #D1D1D1',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <Icon.Document stroke="#000000" />
                              </Box>
                            ),
                            isLoading,
                          )}
                        {fileExtension === 'image' &&
                          renderWithLoader(
                            () => (
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '60px',
                                  height: '60px',
                                  border: '1px solid #D1D1D1',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              ></Box>
                            ),
                            isLoading,
                          )}
                        <IconButton
                          onClick={() => removeReview({ name: file.name, lastModified: file.lastModified })}
                          style={{
                            position: 'absolute',
                            top: '-15px',
                            right: '-15px',
                            cursor: 'pointer',
                            color: '#2055FF',
                            fontSize: '32px',
                          }}
                          sx={{
                            '&:hover': {
                              background: 'none',
                            },
                          }}
                        >
                          <i className="fa-light fa-circle-xmark" style={{ fontSize: '22px', background: '#EAECF0', borderRadius: '50%' }} />
                        </IconButton>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              <Stack
                direction="row"
                sx={{
                  height: '100%',
                  width: '100%',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" sx={{ direction: 'flex', gap: '4px' }}>
                  <IconButton onClick={handleFileIconClick} sx={{ padding: '5px', width: '30px', height: '30px' }}>
                    <i className="fa-regular fa-paperclip" style={{ color: '#667085', fontSize: '18px' }} />
                  </IconButton>
                  <IconButton onClick={handleAtIconClick} sx={{ padding: '5px', width: '30px', height: '30px' }}>
                    <i className="fa-regular fa-at" style={{ fontSize: '18px' }} />
                  </IconButton>
                  <IconButton onClick={recording ? stopRecording : startRecording} sx={{ color: recording ? 'red' : '#667085', padding: '5px', width: '30px', height: '30px' }}>
                    <i className="fa-regular fa-microphone" style={{ fontSize: '18px' }} />
                  </IconButton>
                </Stack>
                {!isNewThread && !editMode && (
                  <IconButton
                    onClick={async () => {
                      setAddButtonDisabled(true);
                      setIsLoading(true);
                      const result = await handleAdd({ description, messageId: null });
                      if (result === 'Success') {
                        clearInput();
                      } else {
                        toast.error('Could not send message');
                        setIsLoading(false);
                      }
                    }}
                    disabled={addButtonDisabled || recording}
                    sx={{
                      mb: '5px',
                      color: 'white',
                      background: '#2055FF',
                      height: '44px',
                      width: '44px',
                      borderRadius: '50%',
                      ml: 'auto',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: '#2055ff',
                        opacity: '0.75',
                      },
                    }}
                  >
                    <i className="fa-regular fa-paper-plane-top" style={{ fontSize: '18px' }} />
                  </IconButton>
                )}
              </Stack>
            </>
          )}
        </Box>

        <input
          type="file"
          accept="image/jpeg, image/png, application/pdf, video/mp4, video/quicktime"
          capture="environment"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          multiple
        />
      </Box>
      {!!descriptionError && isNewThread && (
        <FormHelperText
          sx={{
            textAlign: 'left',
            marginX: 0,
            marginTop: '5px',
            marginLeft: '14px',
            color: 'red'
          }}
        >
          {descriptionError}
        </FormHelperText>
      )}
      {focused && (
        <FormHelperText
          sx={{
            textAlign: 'left',
            marginX: 0,
            marginTop: '5px',
            marginLeft: '14px',
            color: countSymbolsLeft() < 0 ? 'red' : 'inherit',
          }}
        >
          {countSymbolsLeft()} symbols left
        </FormHelperText>
      )}
    </>
  );
};
